import {DOCUMENT} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {BackendErrorCodeEnum} from '@application/helper/backend-error-code.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {ConflictType} from '@domain/conflicts/conflict-type';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {DrawingShapeAbbreviation} from '@domain/production-schedule/drawing-shape-abbreviation';
import {Permission} from '@domain/profile/permission.enum';
import {PropertyValue} from '@domain/property-value';
import {PlaceholderCategory} from '@domain/textile-data/finishing-and-finishing-template/placeholder-category';
import {PlaceholderComponent} from '@domain/textile-data/finishing-and-finishing-template/placeholder-component';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  CapitalFirstLetterPipe,
  Conflict,
  ConflictsDialogComponent,
  ConflictsDialogData,
  DialogBuilderFactoryService,
  EnumUtils,
  FormValidationHelper,
  LocaleUtils,
  ToastService,
  TranslateService,
  WINDOW
} from '@vdw/angular-component-library';
import {L10nIntlService} from 'angular-l10n';
import {isEqual, isNil, lowerCase, some} from 'lodash-es';
import {forkJoin, Observable, of as observableOf, Observer} from 'rxjs';
import {distinctUntilChanged, finalize, switchMap, take, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-drawing-library-custom-settings',
  templateUrl: './drawing-library-custom-settings.component.html',
  styleUrls: ['./drawing-library-custom-settings.component.scss']
})
export class DrawingLibraryCustomSettingsComponent extends BaseComponent implements OnInit {
  public customSettingsForm: FormGroup<{
    widthRoundingPrecision: FormControl<number>;
    heightRoundingPrecision: FormControl<number>;
    bathThreshold: FormControl<number>;
    shape: FormControl<DrawingShapeAbbreviation>;
    shapes: FormArray<FormGroup<{shape: FormControl<string>}>>;
    codes: FormArray<FormGroup<{code: FormControl<string>}>>;
  }>;

  public possibleWidthRoundingPrecisions: number[] = [];
  public possibleHeightRoundingPrecisions: number[] = [];
  public drawingShapes: DrawingShapeAbbreviation[] = [];
  public placeholderParts: PlaceholderComponent[] = [];
  public listOfPlaceholderCategoriesWithComponents: PlaceholderCategory[];
  public separatorForTextPlaceholder = 'UNDERSCORE';
  public addDesignShapePermission: Permission = Permission.ADD_CUSTOM_DESIGN_SHAPE;
  public titleTranslationKey = 'DESIGN_LIBRARY.SETTINGS.DESIGN_SHAPES';

  private saveButtonTouched = false;
  private readonly urlToNavigateToDrawingLibrary = RouteUtils.paths.texEdit.designLibrary.absolutePath;
  private customSettings: PropertyValue[];
  private alreadyAdjustedCustomSettings = false;
  private readonly formValidationHelper: FormValidationHelper = new FormValidationHelper();
  private readonly fixedShapes = EnumUtils.getEnumNames(FixedShape);
  private readonly shapeControlName = 'shape';

  public constructor(
    @Inject(DRAWINGS) private drawingsService: Drawings,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(WINDOW) private readonly window: Window,
    private readonly toastService: ToastService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly capitalFirstLetter: CapitalFirstLetterPipe,
    private readonly navigationHelperService: NavigationHelperService<void>
  ) {
    super();
  }

  public get shapes(): FormArray {
    return this.customSettingsForm.controls.shapes;
  }

  public get codes(): FormArray {
    return this.customSettingsForm.controls.codes;
  }

  public ngOnInit(): void {
    this.setListOfPlaceholderCategoriesWithComponents();
    this.setFormFields();

    forkJoin([this.drawingsService.hasAlreadyAdjustedCustomSettings(), this.drawingsService.getListOfCustomSettings()])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([alreadyAdjustedCustomSettings, settings]: [boolean, PropertyValue[]]) => {
        this.alreadyAdjustedCustomSettings = alreadyAdjustedCustomSettings;
        this.customSettings = settings;
        this.setFormFieldValues();
      });
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.onDestroy();
  }

  public canShowCancelButton(): boolean {
    return this.alreadyAdjustedCustomSettings;
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.urlToNavigateToDrawingLibrary);
  }

  public saveCustomSettings(): void {
    this.saveButtonTouched = true;
    const isValid = this.formValidationHelper.checkForm(this.customSettingsForm, this.document);

    if (isValid) {
      this.saving = true;

      this.customSettings.forEach((property: PropertyValue) => this.setPropertyValue(property));

      this.drawingsService
        .saveCustomSettings(this.customSettings)
        .pipe(finalize(this.finalizeSaving), takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: () => {
            const newItemNames = this.drawingShapes.filter((shape: DrawingShapeAbbreviation) => AssertionUtils.isNullOrUndefined(shape.id)).map((shape: DrawingShapeAbbreviation) => shape.shape);
            if (newItemNames.length > 0) {
              this.navigationHelperService.savePartialState<NavigationNewItemData>({newItemNames});
            }
            this.showToastInfoAndNavigateToDrawingLibrary();
          },
          error: (errorMessage: BackendError) => {
            if (errorMessage.errorContext.errorCode === BackendErrorCodeEnum[BackendErrorCodeEnum.LINKED_ENTITIES]) {
              this.getDrawingShapesConflicts(errorMessage);
            }
          }
        });
    }
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid = some(this.customSettingsForm.controls, (control: FormControl) => control.invalid && control.touched);

    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }

    return isFormInvalid && this.saveButtonTouched;
  }

  public placeholderPartsChanged(placeholderParts: PlaceholderComponent[]): void {
    this.placeholderParts = placeholderParts;
  }

  public onShapeNameChange(index: number): void {
    this.drawingShapes[index].shape = this.shapes.get(`${index}`).value.shape;
  }

  public onShapeCodeChange(index: number): void {
    this.drawingShapes[index].code = this.codes.get(`${index}`).value.code;
  }

  public canShowWovenRepeatedly(shape: string): boolean {
    const shapes = [FixedShape[FixedShape.RECTANGULAR], FixedShape[FixedShape.OVAL], FixedShape[FixedShape.CIRCLE]];
    return !shapes.includes(shape);
  }

  public canDisableWovenRepeatedly(shape: string): boolean {
    const shapes = [FixedShape[FixedShape.WALL_TO_WALL], FixedShape[FixedShape.RUNNER]];
    return shapes.includes(shape);
  }

  public onWovenRepeatedlyChange(index: number): void {
    this.drawingShapes[index].repeated = !this.drawingShapes[index].repeated;
  }

  public isDefaultShape(shape: string): boolean {
    return this.fixedShapes.includes(shape);
  }

  public addDesignShape(): void {
    this.drawingShapes.push(new DrawingShapeAbbreviation(null, '', '', false));
    this.shapes.push(this.formBuilder.group({shape: this.formBuilder.control('', Validators.required, this.uniqueDrawingShapeValidator())}));
    this.codes.push(this.formBuilder.group({code: this.formBuilder.control('', Validators.required, this.uniqueDrawingShapeValidator('code'))}));
  }

  public removeDesignShape(customDrawingShape: DrawingShapeAbbreviation): void {
    const indexToRemove = this.drawingShapes.indexOf(customDrawingShape);
    this.drawingShapes.splice(indexToRemove, 1);
    this.shapes.removeAt(indexToRemove);
    this.codes.removeAt(indexToRemove);

    if (this.customSettingsForm.controls[this.shapeControlName].value === customDrawingShape) {
      const firstShape = this.drawingShapes[0];
      this.customSettingsForm.controls[this.shapeControlName].setValue(firstShape);
    }
  }

  public getLabel(shape: string): string {
    return FixedShape.getTranslationKeyForShape(shape, this.translate);
  }

  private setFormFields(): void {
    this.customSettingsForm = this.formBuilder.group({
      widthRoundingPrecision: this.formBuilder.control(null, Validators.required),
      heightRoundingPrecision: this.formBuilder.control(null, Validators.required),
      bathThreshold: this.formBuilder.control(null, Validators.compose([Validators.required, Validators.min(1), Validators.max(100)])),
      shape: this.formBuilder.control(null, Validators.required),
      shapes: new FormArray([]),
      codes: new FormArray([])
    });
  }

  private setFormFieldValues(): void {
    this.customSettings.forEach((property: PropertyValue) => {
      if (this.isPlaceholderPartsForDrawingNameProperty(property)) {
        this.placeholderParts = property?.propertyValue;
      } else if (this.isShapeAbbreviationsProperty(property)) {
        property?.propertyValue.forEach((value: any) => {
          const drawingShape = new DrawingShapeAbbreviation(value.shapeId, value.code, value.shape, value.repeated);
          this.drawingShapes.push(drawingShape);
          this.shapes.push(this.formBuilder.group({shape: this.formBuilder.control(drawingShape.shape, Validators.required, this.uniqueDrawingShapeValidator())}));
          this.codes.push(this.formBuilder.group({code: this.formBuilder.control(drawingShape.code, Validators.required, this.uniqueDrawingShapeValidator('code'))}));
        });

        const shape = this.customSettings.find((setting: PropertyValue) => setting.propertyName === this.shapeControlName);
        const currentShape = this.drawingShapes.find((value: any) => value.shape === shape?.propertyValue);
        this.customSettingsForm.controls[this.shapeControlName].setValue(currentShape);
      } else if (!AssertionUtils.isNullOrUndefined(this.customSettingsForm.controls[property.propertyName]) && property.propertyName !== this.shapeControlName) {
        this.customSettingsForm.controls[property.propertyName].setValue(property?.propertyValue);

        if (property.propertyName === 'widthRoundingPrecision') {
          this.possibleWidthRoundingPrecisions = property.possiblePropertyValues;
        } else if (property.propertyName === 'heightRoundingPrecision') {
          this.possibleHeightRoundingPrecisions = property.possiblePropertyValues;
        }
      }
    });
  }

  private setListOfPlaceholderCategoriesWithComponents(): void {
    this.drawingsService
      .getCategoriesWithComponents()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfPlaceholderCategoriesWithComponents: PlaceholderCategory[]) => {
        this.listOfPlaceholderCategoriesWithComponents = listOfPlaceholderCategoriesWithComponents;
      });
  }

  private uniqueDrawingShapeValidator(controlName: string = this.shapeControlName): any {
    const errorCode = `unique${this.capitalFirstLetter.transform(controlName)}`;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return new Observable((observer: Observer<string>) => observer.next(control.value)).pipe(
        distinctUntilChanged(),
        take(1),
        switchMap((value: string) => {
          let result: ValidationErrors = null;

          if (control.parent && !isNil(value)) {
            const formArray = control.parent.parent as FormArray;

            if (
              formArray.controls.some((formControl: AbstractControl) => {
                const shapeCodeFormControl = formControl.get(controlName);
                const shapeCodeValue =
                  this.isDefaultShape(shapeCodeFormControl.value) && controlName === this.shapeControlName ? this.getLabel(shapeCodeFormControl.value) : shapeCodeFormControl.value;
                return shapeCodeFormControl !== control && shapeCodeValue?.toLowerCase() === value.toLowerCase() && !shapeCodeFormControl.hasError(errorCode);
              })
            ) {
              result = {[`${errorCode}`]: true};
            }

            this.updateShapeCodeFormControls(control, value, formArray, controlName, errorCode);
          }

          return observableOf(result);
        })
      );
    };
  }

  private updateShapeCodeFormControls(control: AbstractControl, value: string, formArray: FormArray, controlName: string, errorCode: string): void {
    formArray.controls.forEach((formControl: AbstractControl) => {
      const shapeCodeFormControl = formControl.get(controlName);

      if (shapeCodeFormControl !== control && shapeCodeFormControl.hasError(errorCode) && shapeCodeFormControl.value !== value) {
        shapeCodeFormControl.updateValueAndValidity();
      }
    });
  }

  private isShapeAbbreviationsProperty(property: PropertyValue): boolean {
    return property.propertyName === 'shapeAbbreviations';
  }

  private isPlaceholderPartsForDrawingNameProperty(property: PropertyValue): boolean {
    return property.propertyName === 'placeholderPartsForDrawingName';
  }

  private setPropertyValue(property: PropertyValue): void {
    if (this.isPlaceholderPartsForDrawingNameProperty(property)) {
      property.propertyValue = this.placeholderParts;
    } else if (this.isShapeAbbreviationsProperty(property)) {
      property.propertyValue = this.drawingShapes.map((drawingShape: DrawingShapeAbbreviation) => {
        const shapeToReturn = drawingShape.toJSON();

        if (!!drawingShape.id) {
          shapeToReturn['shapeId'] = drawingShape.id;
        }

        return shapeToReturn;
      });

      property.possiblePropertyValues = this.drawingShapes;
    } else if (!isNil(this.customSettingsForm.controls[property.propertyName])) {
      const value = this.customSettingsForm.controls[property.propertyName].value;
      if (property.propertyName === this.shapeControlName) {
        property.propertyValue = this.drawingShapes.find((shape: DrawingShapeAbbreviation) => value.shape === shape.shape).shape;
      } else {
        property.propertyValue = isEqual(property.propertyName, 'bathThreshold') ? LocaleUtils.parseNumber(value, this.l10nIntlService) : value;
      }
    }
  }

  private showToastInfoAndNavigateToDrawingLibrary(): void {
    this.toastService.showInfo({
      tapToDismiss: false,
      timeOut: 2000,
      message: this.translate.instant('GENERAL.ACTIONS.SAVED_OBJECT', {
        object: lowerCase(this.translate.instant('GENERAL.CUSTOM_SETTINGS.CUSTOM_SETTINGS'))
      })
    });

    this.navigateBack();
  }

  private getDrawingShapesConflicts(errorMessage: BackendError): void {
    const listOfConflictNames = errorMessage.errorContext.conflicts.map((conflict: {shape: string; drawings: Conflict[]}) => conflict.shape);
    const listOfConflicts = errorMessage.errorContext.conflicts.flatMap((conflict: {shape: string; drawings: Conflict[]}) => conflict.drawings);

    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('alert-dialog')
      .openDialog(ConflictsDialogComponent, ConflictsDialogData.createCannotDeleteData(this.titleTranslationKey, listOfConflictNames.join(', '), listOfConflicts, ConflictType))
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => this.window.location.reload());
  }
}
